// src/Components/Modules/CompanySetting/EmploymentTypeRules.js
import React, { useState, useEffect, useContext } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import {
  Box, Typography, Paper, Avatar, Button, IconButton, Tooltip,
  Chip, Stack, Switch, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, InputAdornment, Grid, Tabs, Tab, Alert, MenuItem, FormControlLabel
} from "@mui/material";
import {
  FaBriefcase, FaPlus, FaEdit, FaTrash, FaCheckCircle, FaTimesCircle,
  FaClock, FaGift, FaMoneyBillWave, FaUserTie, FaSave, FaUsers,
  FaCalendarAlt, FaHeart, FaHome, FaChartLine, FaDollarSign,
  FaShieldAlt, FaFileAlt, FaInfoCircle, FaGraduationCap, FaHandshake,
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

// Styled Components
const ResponsiveTableWrapper = styled(Box)(({ theme }) => ({
  overflowX: "auto",
  borderRadius: theme.spacing(2),
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
  "&::-webkit-scrollbar": {
    height: 8,
  },
  "&::-webkit-scrollbar-track": {
    background: "#f1f1f1",
    borderRadius: 10,
  },
  "&::-webkit-scrollbar-thumb": {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    borderRadius: 10,
    "&:hover": {
      background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
    },
  },
}));

const ModernTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  fontWeight: 500,
  fontSize: "0.5rem",
  borderBottom: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(1, 1.5),
    fontSize: "0.75rem",
    whiteSpace: "nowrap",
  },
}));

const ModernTableHead = styled(TableHead)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  "& .MuiTableCell-root": {
    color: "white",
    fontWeight: 700,
    fontSize: "0.7rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    lineHeight: "1.2",
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.down("md")]: {
      fontSize: "0.7rem",
      padding: theme.spacing(1, 1.5),
    },
  },
}));

const ModernTableRow = styled(TableRow)(({ theme }) => ({
  "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.04)" },
  transition: "background-color 0.2s ease",
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  background: "white",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
  marginBottom: theme.spacing(3),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
}));

const ToggleCard = styled(Paper)(({ theme, active }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: active ? "2px solid #667eea" : "1px solid rgba(0,0,0,0.08)",
  background: active
    ? "linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)"
    : "white",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#667eea",
    boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: "none",
  fontWeight: 600,
  minHeight: 48,
  fontSize: "0.875rem",
  "&.Mui-selected": {
    color: "#667eea",
    fontWeight: 700,
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "0.75rem",
    minHeight: 40,
    padding: theme.spacing(0.5, 1),
  },
}));

const employmentTypes = [
  { label: "Permanent", value: "permanent", color: "#2196f3" },
  { label: "Contractual", value: "contractual", color: "#ff9800" },
  { label: "Part-Time", value: "part_time", color: "#9c27b0" },
  { label: "Intern", value: "intern", color: "#4caf50" },
  { label: "Probation", value: "probation", color: "#f44336" },
  { label: "Temporary", value: "temporary", color: "#795548" },
  { label: "Freelance", value: "freelance", color: "#00bcd4" },
];

const EmploymentTypeRules = () => {
  const { company } = useContext(MotherContext);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 960);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const defaultFormData = {
    employment_type: "permanent",
    salary_rules: {},
    eligible_for_bonus: true,
    eligible_for_festival_bonus: true,
    eligible_for_overtime: true,
    leave_entitlement: { casual: 12, sick: 14, annual: 20 },
    leave_encashment_allowed: false,
    max_leave_carry_forward: 0,
    provident_fund_eligible: false,
    pf_percentage: 10,
    gratuity_eligible: false,
    health_insurance: false,
    probation_months: 0,
    confirmation_increment_percent: 0,
    resignation_notice_days: 30,
    termination_notice_days: 30,
    severance_months: 0,
    working_days_per_week: 6,
    daily_working_hours: 8,
    work_from_home_allowed: false,
    can_take_loan: false,
    max_loan_amount: 0,
    can_take_advance_salary: false,
    max_advance_percentage: 50,
    is_active: true,
  };

  const [formData, setFormData] = useState({ ...defaultFormData });

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await AxiosAuthInstance.get("company/employment-rules");
      setRules(res.data.rules || []);
    } catch (error) {
      console.error("Failed to fetch rules", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []);

  const handleOpenDialog = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        employment_type: rule.employment_type || "permanent",
        salary_rules: rule.salary_rules || {},
        eligible_for_bonus: rule.eligible_for_bonus ?? true,
        eligible_for_festival_bonus: rule.eligible_for_festival_bonus ?? true,
        eligible_for_overtime: rule.eligible_for_overtime ?? true,
        leave_entitlement: rule.leave_entitlement || { casual: 12, sick: 14, annual: 20 },
        leave_encashment_allowed: rule.leave_encashment_allowed ?? false,
        max_leave_carry_forward: rule.max_leave_carry_forward ?? 0,
        provident_fund_eligible: rule.provident_fund_eligible ?? false,
        pf_percentage: rule.pf_percentage ?? 10,
        gratuity_eligible: rule.gratuity_eligible ?? false,
        health_insurance: rule.health_insurance ?? false,
        probation_months: rule.probation_months ?? 0,
        confirmation_increment_percent: rule.confirmation_increment_percent ?? 0,
        resignation_notice_days: rule.resignation_notice_days ?? 30,
        termination_notice_days: rule.termination_notice_days ?? 30,
        severance_months: rule.severance_months ?? 0,
        working_days_per_week: rule.working_days_per_week ?? 6,
        daily_working_hours: rule.daily_working_hours ?? 8,
        work_from_home_allowed: rule.work_from_home_allowed ?? false,
        can_take_loan: rule.can_take_loan ?? false,
        max_loan_amount: rule.max_loan_amount ?? 0,
        can_take_advance_salary: rule.can_take_advance_salary ?? false,
        max_advance_percentage: rule.max_advance_percentage ?? 50,
        is_active: rule.is_active ?? true,
      });
    } else {
      setEditingRule(null);
      setFormData({ ...defaultFormData });
    }
    setActiveTab(0);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRule(null);
  };

  const handleSave = async () => {
    if (!formData.employment_type) {
      Swal.fire({ icon: "warning", title: "Missing Information", text: "Please select employment type" });
      return;
    }

    setLoading(true);
    try {
      const url = editingRule
        ? `company/employment-rules/${editingRule.id}`
        : "company/employment-rules";
      const method = editingRule ? "put" : "post";

      const res = await AxiosAuthInstance[method](url, formData);

      if (res.data.status) {
        Swal.fire({
          icon: "success",
          title: editingRule ? "Updated!" : "Created!",
          text: `Rules ${editingRule ? "updated" : "created"} successfully`,
          timer: 2000,
          showConfirmButton: false,
        });
        fetchRules();
        handleCloseDialog();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Operation failed",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Rule?",
      text: "This employment type rule will be deleted. Continue?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      cancelButtonColor: "#757575",
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`company/employment-rules/${id}`);
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Rule deleted successfully",
          timer: 2000,
          showConfirmButton: false,
        });
        fetchRules();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Error", text: "Failed to delete rule" });
      }
    }
  };

  const getEmploymentTypeInfo = (value) => {
    return employmentTypes.find(t => t.value === value) || { label: value, color: "#757575" };
  };

  const tabs = [
    { key: "general", label: "General", icon: FaInfoCircle, color: "#2196f3" },
    { key: "salary", label: "Salary & Benefits", icon: FaMoneyBillWave, color: "#4caf50" },
    { key: "leave", label: "Leave Rules", icon: FaCalendarAlt, color: "#9c27b0" },
    { key: "probation", label: "Probation", icon: FaGraduationCap, color: "#ff9800" },
    { key: "work", label: "Work Config", icon: FaClock, color: "#f44336" },
  ];

  return (
    <Box>
      <SectionCard>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Avatar sx={{ bgcolor: "#e3f2fd", color: "#1976d2", mr: 2, width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 } }}>
              <FaUsers size={isMobile ? 16 : 20} />
            </Avatar>
            <Box>
              <Typography variant={isMobile ? "subtitle1" : "h6"} fontWeight={600}>
                Employment Type Specific Rules
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                Configure rules and benefits for each employment type
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            onClick={() => handleOpenDialog()}
            size={isMobile ? "small" : "medium"}
            sx={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              textTransform: "none",
              borderRadius: 2,
              px: { xs: 2, md: 3 },
              py: { xs: 0.8, md: 1.2 },
              fontWeight: 600,
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.4)",
              },
            }}
          >
            Add Employment Rule
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <ResponsiveTableWrapper>
          <TableContainer>
            <Table size={isMobile ? "small" : "medium"}>
              <ModernTableHead>
                <TableRow>
                  <ModernTableCell>Employment Type</ModernTableCell>
                  <ModernTableCell align="center">PF Eligible</ModernTableCell>
                  <ModernTableCell align="center">Festival Bonus</ModernTableCell>
                  <ModernTableCell align="center">Bonus</ModernTableCell>
                  <ModernTableCell align="center">Overtime</ModernTableCell>
                  <ModernTableCell>Probation</ModernTableCell>
                  <ModernTableCell>Notice Period</ModernTableCell>
                  <ModernTableCell align="center">Status</ModernTableCell>
                  <ModernTableCell align="center">Actions</ModernTableCell>
                </TableRow>
              </ModernTableHead>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <ModernTableCell colSpan={9} align="center" sx={{ py: { xs: 4, md: 8 } }}>
                      <Box sx={{ textAlign: "center" }}>
                        <FaBriefcase style={{ fontSize: isMobile ? 40 : 56, color: "#bdbdbd", marginBottom: 16 }} />
                        <Typography variant={isMobile ? "body2" : "h6"} color="text.secondary" gutterBottom>
                          No employment rules configured
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                          Click "Add Employment Rule" to create your first rule
                        </Typography>
                      </Box>
                    </ModernTableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => {
                    const typeInfo = getEmploymentTypeInfo(rule.employment_type);
                    return (
                      <ModernTableRow key={rule.id}>
                        <ModernTableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, md: 1.5 } }}>
                            <Avatar sx={{ bgcolor: `${typeInfo.color}20`, color: typeInfo.color, width: { xs: 32, md: 40 }, height: { xs: 32, md: 40 } }}>
                              <FaUserTie style={{ fontSize: isMobile ? 12 : 16 }} />
                            </Avatar>
                            <Typography fontWeight={600} sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }}>
                              {typeInfo.label}
                            </Typography>
                          </Box>
                        </ModernTableCell>
                        <ModernTableCell align="center">
                          {rule.provident_fund_eligible ? (
                            <Chip
                              icon={<FaCheckCircle style={{ fontSize: 12 }} />}
                              label="Yes"
                              size="small"
                              sx={{
                                bgcolor: "#e8f5e9",
                                color: "#2e7d32",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#4caf50" },
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<FaTimesCircle style={{ fontSize: 12 }} />}
                              label="No"
                              size="small"
                              sx={{
                                bgcolor: "#ffebee",
                                color: "#c62828",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#f44336" },
                              }}
                            />
                          )}
                        </ModernTableCell>
                        <ModernTableCell align="center">
                          {rule.eligible_for_festival_bonus ? (
                            <Chip
                              icon={<FaGift style={{ fontSize: 12 }} />}
                              label="Yes"
                              size="small"
                              sx={{
                                bgcolor: "#fff3e0",
                                color: "#ef6c00",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#ff9800" },
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<FaTimesCircle style={{ fontSize: 12 }} />}
                              label="No"
                              size="small"
                              sx={{
                                bgcolor: "#f5f5f5",
                                color: "#757575",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#9e9e9e" },
                              }}
                            />
                          )}
                        </ModernTableCell>
                        <ModernTableCell align="center">
                          {rule.eligible_for_bonus ? (
                            <Chip
                              icon={<FaChartLine style={{ fontSize: 12 }} />}
                              label="Yes"
                              size="small"
                              sx={{
                                bgcolor: "#e8f5e9",
                                color: "#2e7d32",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#4caf50" },
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<FaTimesCircle style={{ fontSize: 12 }} />}
                              label="No"
                              size="small"
                              sx={{
                                bgcolor: "#ffebee",
                                color: "#c62828",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#f44336" },
                              }}
                            />
                          )}
                        </ModernTableCell>
                        <ModernTableCell align="center">
                          {rule.eligible_for_overtime ? (
                            <Chip
                              icon={<FaCheckCircle style={{ fontSize: 12 }} />}
                              label="Yes"
                              size="small"
                              sx={{
                                bgcolor: "#e3f2fd",
                                color: "#1565c0",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#2196f3" },
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<FaTimesCircle style={{ fontSize: 12 }} />}
                              label="No"
                              size="small"
                              sx={{
                                bgcolor: "#f5f5f5",
                                color: "#757575",
                                fontWeight: 600,
                                fontSize: { xs: "0.65rem", md: "0.75rem" },
                                height: { xs: 22, md: 28 },
                                "& .MuiChip-icon": { color: "#9e9e9e" },
                              }}
                            />
                          )}
                        </ModernTableCell>
                        <ModernTableCell>
                          {rule.probation_months > 0 ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <FaGraduationCap style={{ color: "#ff9800", fontSize: 12 }} />
                              <Typography variant="body2" sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                                {rule.probation_months} months
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                              None
                            </Typography>
                          )}
                        </ModernTableCell>
                        <ModernTableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <FaClock style={{ color: "#667eea", fontSize: 12 }} />
                            <Typography variant="body2" sx={{ fontSize: { xs: "0.7rem", md: "0.875rem" } }}>
                              {rule.resignation_notice_days || 30} days
                            </Typography>
                          </Box>
                        </ModernTableCell>
                        <ModernTableCell align="center">
                          <Chip
                            label={rule.is_active ? "Active" : "Inactive"}
                            size="small"
                            icon={rule.is_active ? <FaCheckCircle /> : <FaTimesCircle />}
                            sx={{
                              bgcolor: rule.is_active ? "#e8f5e9" : "#ffebee",
                              color: rule.is_active ? "#2e7d32" : "#c62828",
                              fontWeight: 600,
                              fontSize: { xs: "0.65rem", md: "0.75rem" },
                              height: { xs: 22, md: 28 },
                              "& .MuiChip-icon": {
                                color: rule.is_active ? "#4caf50" : "#f44336",
                              },
                            }}
                          />
                        </ModernTableCell>
                        <ModernTableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(rule)}
                                sx={{
                                  color: "#667eea",
                                  bgcolor: "rgba(102, 126, 234, 0.1)",
                                  "&:hover": { bgcolor: "rgba(102, 126, 234, 0.2)" },
                                  width: { xs: 28, md: 32 },
                                  height: { xs: 28, md: 32 },
                                }}
                              >
                                <FaEdit style={{ fontSize: isMobile ? 10 : 12 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(rule.id)}
                                sx={{
                                  color: "#f44336",
                                  bgcolor: "rgba(244, 67, 54, 0.1)",
                                  "&:hover": { bgcolor: "rgba(244, 67, 54, 0.2)" },
                                  width: { xs: 28, md: 32 },
                                  height: { xs: 28, md: 32 },
                                }}
                              >
                                <FaTrash style={{ fontSize: isMobile ? 10 : 12 }} />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </ModernTableCell>
                      </ModernTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </ResponsiveTableWrapper>
      </SectionCard>

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            borderRadius: { xs: 0, sm: 3 },
            m: { xs: 0, sm: 2 }
          } 
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            py: { xs: 2, md: 2.5 },
            px: { xs: 2, md: 3 },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaBriefcase />
            </Box>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}>
              {editingRule ? "Edit Employment Rules" : "Add Employment Type Rules"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "#f8f9ff" }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTabs-indicator": {
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  height: 3,
                },
              }}
            >
              {tabs.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <StyledTab
                    key={tab.key}
                    icon={<IconComponent style={{ marginRight: 8 }} />}
                    iconPosition="start"
                    label={tab.label}
                  />
                );
              })}
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, md: 3 } }}>
            {/* General Tab */}
            {activeTab === 0 && (
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    select
                    fullWidth
                    label="Employment Type *"
                    value={formData.employment_type}
                    onChange={(e) => setFormData({ ...formData, employment_type: e.target.value })}
                    disabled={!!editingRule}
                    size={isMobile ? "small" : "medium"}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  >
                    {employmentTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: type.color,
                            }}
                          />
                          {type.label}
                        </Box>
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ToggleCard active={formData.is_active}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ bgcolor: formData.is_active ? "#e8f5e9" : "#ffebee", color: formData.is_active ? "#2e7d32" : "#c62828", width: 36, height: 36 }}>
                          <FaShieldAlt />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" fontWeight={600}>Status</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formData.is_active ? "Currently active" : "Inactive"}
                          </Typography>
                        </Box>
                      </Box>
                      <Switch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                        color="success"
                      />
                    </Box>
                  </ToggleCard>
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" icon={<FaInfoCircle />} sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      Select the employment type and toggle its active status. All other settings can be configured in the tabs above.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}

            {/* Salary & Benefits Tab */}
            {activeTab === 1 && (
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: "#667eea", mb: 2 }}>
                    <FaDollarSign style={{ marginRight: 6 }} />
                    Bonus & Overtime
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { key: "eligible_for_bonus", label: "Eligible for Bonus", icon: FaChartLine },
                      { key: "eligible_for_festival_bonus", label: "Festival Bonus", icon: FaGift },
                      { key: "eligible_for_overtime", label: "Overtime", icon: FaClock },
                    ].map((item) => {
                      const IconComponent = item.icon;
                      return (
                        <Grid item xs={12} md={4} key={item.key}>
                          <ToggleCard active={formData[item.key]}>
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={formData[item.key]}
                                  onChange={(e) => setFormData({ ...formData, [item.key]: e.target.checked })}
                                  color="primary"
                                />
                              }
                              label={
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <IconComponent style={{ color: formData[item.key] ? "#667eea" : "#757575" }} />
                                  <span>{item.label}</span>
                                </Box>
                              }
                            />
                          </ToggleCard>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: "#667eea", mb: 2 }}>
                    <FaShieldAlt style={{ marginRight: 6 }} />
                    Social Benefits
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <ToggleCard active={formData.provident_fund_eligible}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.provident_fund_eligible}
                              onChange={(e) => setFormData({ ...formData, provident_fund_eligible: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FaShieldAlt style={{ color: formData.provident_fund_eligible ? "#667eea" : "#757575" }} />
                              <span>Provident Fund</span>
                            </Box>
                          }
                        />
                      </ToggleCard>
                    </Grid>
                    {formData.provident_fund_eligible && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="PF Percentage"
                          type="number"
                          value={formData.pf_percentage}
                          onChange={(e) => setFormData({ ...formData, pf_percentage: Number(e.target.value) })}
                          size={isMobile ? "small" : "medium"}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <ToggleCard active={formData.gratuity_eligible}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.gratuity_eligible}
                              onChange={(e) => setFormData({ ...formData, gratuity_eligible: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FaHandshake style={{ color: formData.gratuity_eligible ? "#667eea" : "#757575" }} />
                              <span>Gratuity</span>
                            </Box>
                          }
                        />
                      </ToggleCard>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ToggleCard active={formData.health_insurance}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.health_insurance}
                              onChange={(e) => setFormData({ ...formData, health_insurance: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FaHeart style={{ color: formData.health_insurance ? "#e91e63" : "#757575" }} />
                              <span>Health Insurance</span>
                            </Box>
                          }
                        />
                      </ToggleCard>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: "#667eea", mb: 2 }}>
                    <FaMoneyBillWave style={{ marginRight: 6 }} />
                    Loan & Advance
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <ToggleCard active={formData.can_take_loan}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.can_take_loan}
                              onChange={(e) => setFormData({ ...formData, can_take_loan: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FaMoneyBillWave style={{ color: formData.can_take_loan ? "#667eea" : "#757575" }} />
                              <span>Can Take Loan</span>
                            </Box>
                          }
                        />
                      </ToggleCard>
                    </Grid>
                    {formData.can_take_loan && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Max Loan Amount"
                          type="number"
                          value={formData.max_loan_amount}
                          onChange={(e) => setFormData({ ...formData, max_loan_amount: Number(e.target.value) })}
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 0 }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <ToggleCard active={formData.can_take_advance_salary}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.can_take_advance_salary}
                              onChange={(e) => setFormData({ ...formData, can_take_advance_salary: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FaDollarSign style={{ color: formData.can_take_advance_salary ? "#667eea" : "#757575" }} />
                              <span>Advance Salary</span>
                            </Box>
                          }
                        />
                      </ToggleCard>
                    </Grid>
                    {formData.can_take_advance_salary && (
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Max Advance %"
                          type="number"
                          value={formData.max_advance_percentage}
                          onChange={(e) => setFormData({ ...formData, max_advance_percentage: Number(e.target.value) })}
                          size={isMobile ? "small" : "medium"}
                          InputProps={{
                            endAdornment: <InputAdornment position="end">%</InputAdornment>,
                          }}
                          inputProps={{ min: 0, max: 100 }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Leave Rules Tab */}
            {activeTab === 2 && (
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: "#667eea", mb: 2 }}>
                    <FaCalendarAlt style={{ marginRight: 6 }} />
                    Leave Entitlement (days/year)
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { key: "casual", label: "Casual Leave", icon: "🏖️", color: "#2196f3" },
                      { key: "sick", label: "Sick Leave", icon: "🏥", color: "#f44336" },
                      { key: "annual", label: "Annual Leave", icon: "🌴", color: "#4caf50" },
                    ].map((leave) => (
                      <Grid item xs={12} md={4} key={leave.key}>
                        <TextField
                          fullWidth
                          label={`${leave.icon} ${leave.label}`}
                          type="number"
                          value={formData.leave_entitlement?.[leave.key] || 0}
                          onChange={(e) => setFormData({
                            ...formData,
                            leave_entitlement: {
                              ...formData.leave_entitlement,
                              [leave.key]: Number(e.target.value),
                            },
                          })}
                          size={isMobile ? "small" : "medium"}
                          inputProps={{ min: 0, max: 365 }}
                          helperText="Days per year"
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: "#667eea", mb: 2 }}>
                    <FaFileAlt style={{ marginRight: 6 }} />
                    Leave Settings
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <ToggleCard active={formData.leave_encashment_allowed}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.leave_encashment_allowed}
                              onChange={(e) => setFormData({ ...formData, leave_encashment_allowed: e.target.checked })}
                              color="primary"
                            />
                          }
                          label="Leave Encashment Allowed"
                        />
                      </ToggleCard>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Max Leave Carry Forward"
                        type="number"
                        value={formData.max_leave_carry_forward}
                        onChange={(e) => setFormData({ ...formData, max_leave_carry_forward: Number(e.target.value) })}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: 0, max: 365 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Probation Tab */}
            {activeTab === 3 && (
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Probation Period (Months)"
                    type="number"
                    value={formData.probation_months}
                    onChange={(e) => setFormData({ ...formData, probation_months: Number(e.target.value) })}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaGraduationCap style={{ color: "#757575" }} />
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0, max: 24 }}
                    helperText="0 = No probation"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confirmation Increment"
                    type="number"
                    value={formData.confirmation_increment_percent}
                    onChange={(e) => setFormData({ ...formData, confirmation_increment_percent: Number(e.target.value) })}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FaChartLine style={{ color: "#757575" }} />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info" icon={<FaInfoCircle />} sx={{ borderRadius: 2 }}>
                    <Typography variant="body2">
                      After probation period ends, employee will be confirmed with the specified increment percentage.
                    </Typography>
                  </Alert>
                </Grid>
              </Grid>
            )}

            {/* Work Config Tab */}
            {activeTab === 4 && (
              <Grid container spacing={{ xs: 2, md: 3 }}>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: "#667eea", mb: 2 }}>
                    <FaClock style={{ marginRight: 6 }} />
                    Work Schedule
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Working Days/Week"
                        type="number"
                        value={formData.working_days_per_week}
                        onChange={(e) => setFormData({ ...formData, working_days_per_week: Number(e.target.value) })}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: 1, max: 7 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Daily Working Hours"
                        type="number"
                        value={formData.daily_working_hours}
                        onChange={(e) => setFormData({ ...formData, daily_working_hours: Number(e.target.value) })}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: 1, max: 24 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <ToggleCard active={formData.work_from_home_allowed}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.work_from_home_allowed}
                              onChange={(e) => setFormData({ ...formData, work_from_home_allowed: e.target.checked })}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <FaHome style={{ color: formData.work_from_home_allowed ? "#4caf50" : "#757575" }} />
                              <span>Work From Home</span>
                            </Box>
                          }
                        />
                      </ToggleCard>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ color: "#667eea", mb: 2 }}>
                    <FaFileAlt style={{ marginRight: 6 }} />
                    Separation Rules
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Resignation Notice (Days)"
                        type="number"
                        value={formData.resignation_notice_days}
                        onChange={(e) => setFormData({ ...formData, resignation_notice_days: Number(e.target.value) })}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: 0, max: 365 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Termination Notice (Days)"
                        type="number"
                        value={formData.termination_notice_days}
                        onChange={(e) => setFormData({ ...formData, termination_notice_days: Number(e.target.value) })}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: 0, max: 365 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Severance (Months of Basic)"
                        type="number"
                        value={formData.severance_months}
                        onChange={(e) => setFormData({ ...formData, severance_months: Number(e.target.value) })}
                        size={isMobile ? "small" : "medium"}
                        inputProps={{ min: 0, max: 24, step: 0.5 }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, md: 2.5 }, pt: 1, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2,
              borderColor: "#757575",
              color: "#757575",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            variant="contained"
            size={isMobile ? "small" : "medium"}
            startIcon={loading ? null : <FaSave />}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              py: 1.2,
              borderRadius: 2,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              "&:hover": {
                background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
                boxShadow: "0 4px 12px rgba(102, 126, 234, 0.4)",
              },
            }}
          >
            {loading ? "Saving..." : editingRule ? "Update Rules" : "Save Rules"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmploymentTypeRules;